import { useCallback, useEffect, useState } from "react";

import { DataTable, DataTableCellSelection } from "primereact/datatable";
import { Column } from "primereact/column";
import { KeyValue, Status } from "../../ProviderProfile/models";
import httpService from "../../../../services/httpService";
import { ApiEndpoints, Constants } from "../../../../constants/appConstants";



export interface PfpRHProvidersKpiDashboardProps {
  providersData: any;
  searchHandler: (query: any, columns: any) => void;
}

export const PfpRHKpiDashboardComponent = ({
  providersData,
  searchHandler,
}: PfpRHProvidersKpiDashboardProps) => {
  const [providersPerformanceData, setProvidersPerformanceData] = useState<
    Record<string, string>[]
  >([]);
  const [providersTiersData, setProvidersTiersData] = useState<
    Record<string, string>[]
  >([]);
  const [selectedCell, setSelectedCell] = useState<DataTableCellSelection<
    KeyValue[]
  > | null>(null);
  const [selectedTierCell, setSelectedTierCell] =
    useState<DataTableCellSelection<KeyValue[]> | null>(null);

  const [tiers, setAllTiers] = useState<any[]>([]);
  const [statuses, setAllStatues] = useState<any[]>([]);


  useEffect(() => {
    getAllTiers();
    getAllStatuses()
  }, []);

  const getAllTiers = () => {
    httpService.get<any[]>(`${ApiEndpoints.GetTiers}?providerType=${Constants.RSKey}`).then((response) => {
      setAllTiers(response.data);
    }).catch((error) => {
      console.error('Error fetching tiers:', error);
    });
  }

  const getAllStatuses = () => {
    httpService.get<Status[]>(`${ApiEndpoints.GetStatus}?providerType=${Constants.RSKey}`).then((response) => {
      setAllStatues(response.data);
    }).catch((error) => {
      console.error('Error fetching tiers:', error);
    });
  }

  const setKpiData = useCallback(
    (rowData: []) => {
      const performanceData: Record<string, string>[] = [];
      const performanceObj: Record<string, string> = {
        providerIndicator: "Count",
      };
      const percentageObj: Record<string, string> = {
        providerIndicator: "Percentage",
      };
      for (const status of statuses) {
        const count = rowData.filter(
          (provider: any) =>
            provider.reviewStatusCode?.toLowerCase() == status.statusCode?.toLowerCase()
        ).length;
        performanceObj[status.statusCode] = count > 0 ? String(count) : '-';
        const avg = count > 0 ? ((count / rowData.length) * 100) : 0;
        percentageObj[status.statusCode] = avg > 0 ? String(avg.toFixed(2)) + "%" : '-';
      }
      performanceObj["Total"] = rowData.length > 0 ? String(rowData.length) : "-";
      percentageObj["Total"] = rowData.length > 0 ? "100%" : "-";
      performanceData.push(performanceObj);
      performanceData.push(percentageObj);
      setProvidersPerformanceData(performanceData);
    },
    [providersData]
  );

  const setTiersKpiData = useCallback(
    (rowData: []) => {
      const tiersData: Record<string, string>[] = [];
      const tierObj: Record<string, string> = {
        providerIndicator: "Count",
      };
      const percentageObj: Record<string, string> = {
        providerIndicator: "Percentage",
      };
      for (const tier of tiers) {
        const count = rowData.filter(
          (provider: any) =>
            provider.currentTierCode?.toLowerCase() == tier.tierCode.toLowerCase()
        ).length;
        tierObj[tier.tierCode] = count > 0 ? String(count) : '-';

        const avg = count > 0 ? ((count / rowData.length) * 100) : 0;
        percentageObj[tier.tierCode] = avg > 0 ? String(avg.toFixed(2)) + "%" : '-';
      }
      tierObj["Total"] = rowData.length > 0 ? String(rowData.length) : "-";
      percentageObj["Total"] = rowData.length > 0 ? "100%" : "-";
      tiersData.push(tierObj);
      tiersData.push(percentageObj);
      setProvidersTiersData(tiersData);
    },
    [providersData]
  );
  useEffect(() => {
    setKpiData(providersData);
    setTiersKpiData(providersData);
  }, [providersData, setKpiData, setTiersKpiData]);

  const handleCellSelection = (e: any) => {
    if (
      e.value !== null &&
      e.value.column.props.header !== "Provider Review Status" &&
      e.value.column.props.header !== "Total"
    ) {
      setSelectedCell(e.value);
      searchHandler(e.value.column.props.header, ['reviewStatus']);
    } else if (e.value === null || e.value.column.props.header === "Total") {
      setSelectedCell(null);
      searchHandler("", ['reviewStatus']);
    }
    setSelectedTierCell(null);
  };
  const handleTierCellSelection = (e: any) => {
    if (
      e.value !== null &&
      e.value?.column?.props?.header !== "Provider Tier Status" &&
      e.value?.column?.props?.header !== "Total"
    ) {
      searchHandler(e.value.column.props.header, ['currentTier']);
      setSelectedTierCell(e.value);
    } else if (e.value === null || e.value.column.props.header === "Total") {
      setSelectedTierCell(null);
      searchHandler("", ['currentTier']);
    }
    setSelectedCell(null);
  };

  return (
    <div className="kpi-table-container">
      <DataTable
        value={providersPerformanceData}
        cellSelection
        selectionMode="single"
        selection={selectedCell}
        onSelectionChange={(e: any) => handleCellSelection(e)}
        size="small"
        className="kpi-table"
      >
        <Column
          field="providerIndicator"
          header="Provider Review Status"
          className="column-strong"
          headerClassName="kpi-table-header column-align-left"
        ></Column>

        {statuses.sort((a, b) => a.sequence - b.sequence).map((status: Status) =>
          <Column
            field={status.statusCode}
            header={status.statusName}
            headerClassName="kpi-table-header"
            bodyClassName="column-align-center"
          ></Column>
        )}


        <Column
          field="Total"
          header="Total"
          className="column-strong"
          headerClassName="kpi-table-header"
          bodyClassName="column-align-center"
        ></Column>
      </DataTable>

      <DataTable
        value={providersTiersData}
        cellSelection
        selectionMode="single"
        selection={selectedTierCell}
        onSelectionChange={(e: any) => handleTierCellSelection(e)}
        size="small"
        className="kpi-table"
      >
        <Column
          field="providerIndicator"
          header="Provider Tier Status"
          className="column-strong"
          headerClassName="kpi-table-header column-align-left"
        ></Column>


        {tiers.sort((a, b) => a.sequence - b.sequence).map((tier: any) =>
          <Column
            field={tier.tierCode}
            header={tier.tierName}
            headerClassName="kpi-table-header"
            bodyClassName="column-align-center"
          ></Column>
        )}
        <Column
          field="Total"
          header="Total"
          className="column-strong"
          headerClassName="kpi-table-header"
          bodyClassName="column-align-center"
        ></Column>
      </DataTable>
    </div>
  );
};
